import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/Subscription.css';

const Subscription = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [rawUsers, setRawUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.selectedUser) {
      const u = location.state.selectedUser;
      // Format user data to match the component's internal structure
      const formatted = {
        id: u.id,
        name: u.firstName ? `${u.firstName} ${u.lastName}` : (u.name || ''),
        email: u.email,
        amount: 1.99,
        status: u.subscriptionStatus === 'active' ? 'Paid' : 'Unpaid',
        plan: 'Monthly',
        dateJoined: new Date(u.createdAt).toLocaleDateString('en-US', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        }),
        nextBilling: u.subscriptionEndsAt
          ? new Date(u.subscriptionEndsAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
          : 'N/A',
        history: (u.subscriptionPayments || []).map(p => ({
          date: new Date(p.createdAt).toLocaleDateString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
          }),
          amount: parseFloat(p.amount || 0),
          status: p.status === 'active' || p.status === 'succeeded' ? 'Success' : p.status,
          invoiceId: p.stripeInvoiceId
        }))
      };
      setSelectedUser(formatted);
    }
  }, [location.state]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Sync selectedUser with updated users list (to get history if navigating from other pages)
  useEffect(() => {
    if (users.length > 0 && selectedUser && selectedUser.history?.length === 0) {
      const fullUser = users.find(u => u.id === selectedUser.id);
      if (fullUser && fullUser.history?.length > 0) {
        setSelectedUser(fullUser);
      }
    }
  }, [users, selectedUser]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin-management/users');

      const apiUsers = res.data?.users || [];
      setRawUsers(apiUsers);

      const formatted = apiUsers.map(u => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
        email: u.email,
        amount: 1.99, // Assuming fixed amount for now
        status: u.subscriptionStatus === 'active' ? 'Paid' : 'Unpaid',
        plan: 'Monthly',
        dateJoined: new Date(u.createdAt).toLocaleDateString('en-US', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        }),
        nextBilling: u.subscriptionEndsAt
          ? new Date(u.subscriptionEndsAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
          : 'N/A',
        history: (u.subscriptionPayments || []).map(p => ({
          date: new Date(p.createdAt).toLocaleDateString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
          }),
          amount: parseFloat(p.amount || 0),
          status: p.status === 'active' || p.status === 'succeeded' ? 'Success' : p.status,
          invoiceId: p.stripeInvoiceId
        }))
      }));

      setUsers(formatted);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (user) => {
    try {
      setCancelLoading(true);
      const res = await api.post(`/admin-management/users/${user.id}/cancel-subscription`);

      alert(res.data?.message || 'Subscription cancelled successfully.');

      // Update local state for the listing
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, status: 'Unpaid' } : u
      ));
      setRawUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, subscriptionStatus: 'inactive' } : u
      ));

      // Update the selected user detail view
      if (selectedUser?.id === user.id) {
        setSelectedUser(prev => ({ ...prev, status: 'Unpaid' }));
      }

      setShowCancelModal(false);
    } catch (err) {
      console.error('Cancel sub error:', err);
      const errMsg = err.response?.data?.error || 'Failed to cancel subscription';
      alert(errMsg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleExportCSV = (specificUserId = null) => {
    let targetUsers = rawUsers;

    if (specificUserId !== null) {
      targetUsers = rawUsers.filter(u => u.id === specificUserId);
    } else if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      targetUsers = rawUsers.filter(u => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      });
    }

    if (targetUsers.length === 0) {
      alert('No user data available to export.');
      return;
    }

    const headers = [
      'User ID',
      'First Name',
      'Last Name',
      'Email',
      'Role',
      'Is Active',
      'Subscription Status',
      'User Created At',
      'Subscription Ends At',
      'Plaid Status'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };

    const rows = targetUsers.map(u => [
      u.id ?? '',
      u.firstName ?? '',
      u.lastName ?? '',
      u.email ?? '',
      u.role ?? '',
      u.isActive !== undefined && u.isActive !== null ? u.isActive : '',
      u.subscriptionStatus ?? '',
      u.createdAt ?? '',
      u.subscriptionEndsAt ?? '',
      u.plaidStatus !== undefined && u.plaidStatus !== null ? u.plaidStatus : ''
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const filename = specificUserId
      ? `user_${specificUserId}_subscription_${new Date().toISOString().slice(0, 10)}.csv`
      : `subscriptions_${new Date().toISOString().slice(0, 10)}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPaymentHistory = (user) => {
    const rawUser = rawUsers.find(u => u.id === user.id);
    const payments = rawUser?.subscriptionPayments || [];

    if (payments.length === 0 && (!user.history || user.history.length === 0)) {
      alert('No payment history available to export.');
      return;
    }

    const headers = [
      'Payment ID',
      'User ID',
      'User Name',
      'User Email',
      'Stripe Subscription ID',
      'Stripe Invoice ID',
      'Amount',
      'Subscription Payment Status',
      'Billing Reason',
      'Period Start',
      'Period End'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };

    let rows = [];

    if (payments.length > 0) {
      rows = payments.map(p => [
        p.id ?? '',
        p.userId ?? user.id,
        user.name || `${rawUser?.firstName || ''} ${rawUser?.lastName || ''}`.trim(),
        user.email || rawUser?.email || '',
        p.stripeSubscriptionId ?? '',
        p.stripeInvoiceId ?? '',
        p.amount ?? '',
        p.status ?? '',
        p.billingReason ?? '',
        p.periodStart ?? '',
        p.periodEnd ?? ''
      ]);
    } else if (user.history && user.history.length > 0) {
      rows = user.history.map((h, idx) => [
        idx + 1,
        user.id,
        user.name,
        user.email,
        '',
        h.invoiceId || '',
        h.amount,
        h.status,
        '',
        '',
        ''
      ]);
    }

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const filename = `payment_history_user_${user.id}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportAllSubscriptionPayments = () => {
    let targetUsers = rawUsers;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      targetUsers = rawUsers.filter(u => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      });
    }

    if (targetUsers.length === 0) {
      alert('No user data available to export.');
      return;
    }

    const headers = [
      'Payment ID',
      'User ID',
      'User Name',
      'User Email',
      'Stripe Subscription ID',
      'Stripe Invoice ID',
      'Amount',
      'Subscription Payment Status',
      'Billing Reason',
      'Period Start',
      'Period End'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };

    const rows = [];

    targetUsers.forEach(u => {
      const userName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'N/A';
      const userEmail = u.email || '';

      if (Array.isArray(u.subscriptionPayments) && u.subscriptionPayments.length > 0) {
        u.subscriptionPayments.forEach(p => {
          rows.push([
            p.id ?? '',
            p.userId ?? u.id,
            userName,
            userEmail,
            p.stripeSubscriptionId ?? '',
            p.stripeInvoiceId ?? '',
            p.amount ?? '',
            p.status ?? '',
            p.billingReason ?? '',
            p.periodStart ?? '',
            p.periodEnd ?? ''
          ]);
        });
      }
    });

    if (rows.length === 0) {
      alert('No subscription payments found for export.');
      return;
    }

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const filename = `all_subscription_payments_${new Date().toISOString().slice(0, 10)}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (selectedUser) {
    return (
      <div className="subscription-container detail-view">
        <div className="subscription-header">
          <button
            className="back-pill"
            onClick={() => {
              if (location.state?.from) {
                navigate(location.state.from);
              } else {
                setSelectedUser(null);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            <span>Back</span>
          </button>
          <div className="user-profile-summary">
            <div className="profile-avatar">{selectedUser.name.charAt(0)}</div>
            <div>
              <h2>{selectedUser.name}</h2>
              <p>{selectedUser.email}</p>
            </div>
          </div>
          <div className="header-actions-group" style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn-export-csv"
              onClick={() => handleExportCSV(selectedUser.id)}
              disabled={loading || rawUsers.length === 0}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Export CSV</span>
            </button>
            {selectedUser.status === 'Paid' && (
              <button className="btn-cancel-sub" onClick={() => setShowCancelModal(true)}>
                Cancel Subscription
              </button>
            )}
          </div>
        </div>

        <div className="subscription-summary-cards">
          <div className="summary-card">
            <span className="label">Current Plan</span>
            <span className="value">{selectedUser.plan}</span>
          </div>
          <div className="summary-card">
            <span className="label">Monthly Amount</span>
            <span className="value">${selectedUser.amount.toFixed(2)}</span>
          </div>
          <div className="summary-card">
            <span className="label">Status</span>
            <span className={`status-tag ${selectedUser.status === 'Paid' ? 'completed' : 'delayed'}`}>{selectedUser.status}</span>
          </div>
          <div className="summary-card">
            <span className="label">Next Billing Date</span>
            <span className="value">{selectedUser.nextBilling}</span>
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h3 className="card-title">Payment History</h3>
            <button
              className="btn-export-csv"
              onClick={() => handleExportPaymentHistory(selectedUser)}
              title="Export Subscription Payment History CSV"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Export Payment History CSV</span>
            </button>
          </div>
          <div className="table-responsive">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice ID</th>
                </tr>
              </thead>
              <tbody>
                {selectedUser.history.length > 0 ? (
                  selectedUser.history.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        {new Date(item.date).toLocaleDateString('en-US')}
                      </td>
                      <td>${item.amount.toFixed(2)}</td>
                      <td>
                        <span className={`status-chip ${item.status === 'Success' ? 'completed' : 'delayed'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <div className="invoice-cell">
                          <span className="invoice-text">{item.invoiceId || 'N/A'}</span>

                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                      No payment history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showCancelModal && (
          <div className="modal-overlay">
            <div className="modal-content confirmation-modal">
              <div className="modal-header">
                <h3>Cancel Subscription</h3>
                <button className="btn-close" onClick={() => setShowCancelModal(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to cancel <strong>{selectedUser.name}'s</strong> subscription? This action will stop all future billing.</p>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={() => !cancelLoading && setShowCancelModal(false)} disabled={cancelLoading}>Keep Subscription</button>
                  <button className="btn-danger" onClick={() => handleCancelSubscription(selectedUser)} disabled={cancelLoading}>
                    {cancelLoading ? 'Cancelling...' : 'Yes, Cancel Subscription'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="subscription-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '24px' }}>
        <div className="overview-section">
          <div className="overview-header">
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', fontWeight: '500', margin: 0 }}>View and manage user subscriptions</p>
          </div>
        </div>

        <div className="sub-header-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent:'end' }}>
          <div className="search-container visible-search">
            <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or email..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            className="btn-export-csv"
            onClick={() => handleExportCSV()}
            disabled={loading || rawUsers.length === 0}
            title="Export CSV of all user fields"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Export Users CSV</span>
          </button>
          <button
            className="btn-export-csv"
            onClick={() => handleExportAllSubscriptionPayments()}
            disabled={loading || rawUsers.length === 0}
            title="Export CSV of all subscription payments"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Export Payments CSV</span>
          </button>
        </div>
      </div>

      <div className="card">


        <div className="table-responsive">
          <table className="summary-table sub-payments-table">
            <thead>
              <tr>
                <th>User</th>
                <th className="hide-mobile">Date Joined</th>
                <th className="hide-mobile">Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="hide-mobile">Next Billing</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="loading-spinner-container">
                      <p>Loading subscriptions...</p>
                    </div>
                  </td>
                </tr>
              ) : users.filter(u =>
                u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.email.toLowerCase().includes(searchTerm.toLowerCase())
              ).length > 0 ? (
                users.filter(u =>
                  u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  u.email.toLowerCase().includes(searchTerm.toLowerCase())
                ).map((user) => (
                  <tr
                    key={user.id}
                    className="sub-row clickable-row"
                    onClick={() => setSelectedUser(user)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="user-info-stack-row">
                        <div className="user-avatar-mini desktop-only">{user.name.charAt(0)}</div>
                        <div className="user-meta">
                          <div className="user-name-bold">{user.name}</div>
                          <div className="user-email-gray">{user.email}</div>
                        </div>
                      </div>
                    </td>


                    <td className="hide-mobile">
                      {user.dateJoined && user.dateJoined !== 'N/A'
                        ? new Date(user.dateJoined).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                        })
                        : 'N/A'}
                    </td>


                    <td className="hide-mobile">{user.plan}</td>
                    <td>${user.amount.toFixed(2)}</td>
                    <td>
                      <span className={`status-chip ${user.status === 'Paid' ? 'completed' : 'delayed'}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="hide-mobile">
                      {user.nextBilling && user.nextBilling !== 'N/A'
                        ? new Date(user.nextBilling).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                        })
                        : 'N/A'}
                    </td>



                    <td>
                      <button className="btn-list-action" title="View History" onClick={(e) => { e.stopPropagation(); setSelectedUser(user); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    No subscriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Subscription;

